import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  SelectGroup,
  SelectListType,
  TextAreaInput,
} from '@aqarmap/aqarmap-ui';
import Modal from '~/components/Modal';
import {
  useCancelSubscriptionMutation,
  useLazyCancelSurveyOptionsQuery,
} from '~/redux/api/subscriptionApiSlice';
import { closeModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { CancelSubscriptionReasonsEnum } from '~myAccount/pages/UserSubscription/enums';
import ListLoader from '~/components/ListLoader';

type FormData = {
  reasons: string[];
  comment: string;
};

export default function CancelSubscriptionModal({
  renewalDate,
  id,
}: {
  renewalDate: string;
  id: number;
}) {
  const { t } = useTranslation('app');
  const dispatch = useAppDispatch();
  const [reasonsError, setReasonsError] = useState<string | undefined>();
  const [commentError, setCommentError] = useState<string | undefined>();
  const [optionsList, setOptionsList] = useState<SelectListType[] | null>(null);
  const [formData, setFormData] = useState<FormData>({
    reasons: [],
    comment: '',
  });
  const modals = useAppSelector((state) => state.uiState.modals);

  const [cancelSurveyOptions, { isLoading: isOptionsLoading }] =
    useLazyCancelSurveyOptionsQuery();

  const [cancelSubscription, { isLoading }] = useCancelSubscriptionMutation();

  const isCommentInputVisible = () =>
    formData.reasons.includes(CancelSubscriptionReasonsEnum.OTHER);

  const handleUnsubscribe = async () => {
    if (!formData.reasons || !formData.reasons.length) {
      setReasonsError(t('requiredReason'));
      return;
    }
    if (isCommentInputVisible() && !formData.comment) {
      setCommentError(t('requiredComment'));
      return;
    }

    const response = await cancelSubscription({
      id,
      formData,
    });
    if ('data' in response) {
      dispatch(closeModal('cancelSubscriptionModal'));
    }
  };

  const handleFetchSurveyOptions = useCallback(async () => {
    const response = await cancelSurveyOptions(id);
    const list: SelectListType[] =
      response.data?.reasons?.map((reason) => ({
        label: { text: reason.label },
        field: { value: reason.value },
      })) || [];
    setOptionsList(list);
  }, [cancelSurveyOptions, id]);

  useEffect(() => {
    if (modals.cancelSubscriptionModal && !optionsList)
      handleFetchSurveyOptions();
  }, [modals.cancelSubscriptionModal, handleFetchSurveyOptions]);

  return (
    <Modal
      modalKey="cancelSubscriptionModal"
      title={t('cancelSubscription')}
      Footer={
        <div className="flex gap-1.5x w-full">
          <Button
            variant="primary"
            size="medium"
            className="flex-1"
            isLoading={isLoading}
            onClick={handleUnsubscribe}
          >
            {t('okay')}
          </Button>
          <Button
            variant="secondary"
            size="medium"
            className="flex-1"
            onClick={() => dispatch(closeModal('cancelSubscriptionModal'))}
          >
            {t('back')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3x">
        <p className="text-title_4 text-black">
          {
            <Trans
              t={t}
              i18nKey="cancelSubscriptionNote"
              values={{
                date: renewalDate,
              }}
            />
          }
        </p>
        {isOptionsLoading ? (
          <ListLoader count={5} height={24} />
        ) : optionsList && optionsList.length > 0 ? (
          <SelectGroup
            size="medium"
            isMultiple={true}
            name="reason"
            list={optionsList || []}
            handleChange={(e, selectedFields) => {
              setFormData((prev) => ({ ...prev, reasons: selectedFields }));
              if (selectedFields.length) setReasonsError('');
            }}
            error={reasonsError}
            label={{
              text: t('whyCanceling'),
              className: 'text-link',
            }}
          />
        ) : null}
        {isCommentInputVisible() ? (
          <TextAreaInput
            field={{
              name: 'comment',
              placeholder: t('explainIssue'),
              required: true,
              className: 'resize-none',
              onChange: (e) => {
                setFormData((prev) => ({
                  ...prev,
                  comment: e.target.value,
                }));
                if (e.target.value) {
                  setCommentError('');
                }
              },
            }}
            label={{
              text: t('explainIssue'),
              className: 'text-title_4',
            }}
            error={commentError}
          />
        ) : null}
      </div>
    </Modal>
  );
}
